import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { api, ApiError, type FolderRow, type ObjectMeta, type ObjectRow } from '@/lib/api'

export interface Listing {
  folders: FolderRow[]
  objects: ObjectRow[]
  /** Retention state keyed by object key; fills in shortly after the rows. */
  metas: Map<string, ObjectMeta>
  loading: boolean
  loadingMore: boolean
  error: string | null
  hasMore: boolean
  loadMore: () => void
  refresh: () => void
  /** Replaces one object's metadata, e.g. after opening the detail drawer. */
  setMeta: (meta: ObjectMeta) => void
  removeKeys: (keys: string[]) => void
}

/**
 * Loads one "folder" of a bucket.
 *
 * Retention state is fetched in a second pass rather than as part of the
 * listing: S3's ListObjectsV2 does not return HCP's X-HCP-* headers, so lock
 * status requires a HEAD per object. Rows therefore render immediately and
 * lock badges appear a moment later, instead of the whole table waiting on a
 * batch of HEAD requests.
 */
export function useListing(bucket: string | null, prefix: string): Listing {
  const [folders, setFolders] = useState<FolderRow[]>([])
  const [objects, setObjects] = useState<ObjectRow[]>([])
  const [metas, setMetas] = useState<Map<string, ObjectMeta>>(new Map())
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  // Guards against a slow response for a folder the user has already left
  // overwriting the listing they are now looking at.
  const requestId = useRef(0)

  const fetchMetas = useCallback(
    async (targetBucket: string, keys: string[], id: number) => {
      if (keys.length === 0) return
      try {
        const res = await api.metaBatch(targetBucket, keys)
        if (requestId.current !== id) return
        setMetas((current) => {
          const next = new Map(current)
          for (const meta of res.metas) next.set(meta.key, meta)
          return next
        })
      } catch {
        // Lock badges are an enhancement; a failure here must not break
        // browsing. The detail drawer still fetches metadata on demand.
      }
    },
    [],
  )

  const load = useCallback(
    async (continuation: string | null) => {
      if (!bucket) return
      const id = ++requestId.current

      if (continuation) setLoadingMore(true)
      else {
        setLoading(true)
        setError(null)
      }

      try {
        const page = await api.list(bucket, prefix, continuation)
        if (requestId.current !== id) return

        setFolders((current) => (continuation ? [...current, ...page.folders] : page.folders))
        setObjects((current) => (continuation ? [...current, ...page.objects] : page.objects))
        if (!continuation) setMetas(new Map())
        setToken(page.nextToken)

        void fetchMetas(
          bucket,
          page.objects.map((o) => o.key),
          id,
        )
      } catch (err) {
        if (requestId.current !== id) return
        setError(err instanceof ApiError ? err.message : 'Could not load this folder')
      } finally {
        if (requestId.current === id) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [bucket, prefix, fetchMetas],
  )

  useEffect(() => {
    setFolders([])
    setObjects([])
    setMetas(new Map())
    setToken(null)
    void load(null)
  }, [load, nonce])

  const loadMore = useCallback(() => void load(token), [load, token])

  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  const setMeta = useCallback((meta: ObjectMeta) => {
    setMetas((current) => {
      const next = new Map(current)
      next.set(meta.key, meta)
      return next
    })
  }, [])

  const removeKeys = useCallback((keys: string[]) => {
    const removed = new Set(keys)
    setObjects((current) => current.filter((o) => !removed.has(o.key)))
    setMetas((current) => {
      const next = new Map(current)
      for (const key of keys) next.delete(key)
      return next
    })
  }, [])

  // Memoised, and every callback above is stable.
  //
  // This previously returned a fresh object literal on each render. Callers
  // deriving a useCallback from it therefore got a new function identity every
  // render, which re-triggered their effects, which set state, which rendered
  // again — an unbounded loop that fired a HEAD request per iteration.
  return useMemo(
    () => ({
      folders,
      objects,
      metas,
      loading,
      loadingMore,
      error,
      hasMore: token !== null,
      loadMore,
      refresh,
      setMeta,
      removeKeys,
    }),
    [
      folders,
      objects,
      metas,
      loading,
      loadingMore,
      error,
      token,
      loadMore,
      refresh,
      setMeta,
      removeKeys,
    ],
  )
}
